import { useMemo } from 'react';
import { Button, Field, MultiCombobox, type MultiComboboxOption } from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { EmailLink } from '@/components/ui/TextLink';
import type { ListPreview, MarketingList } from '@/lib/types';

export interface AudienceListRef {
  id: number;
  name: string;
  kind: 'segment' | 'static';
}

export interface AudiencePickerProps {
  /** Non-archived lists, or `null` while loading / after a failed read. */
  lists: MarketingList[] | null;
  listsLoading: boolean;
  listsError: string | null;
  /** True when more lists exist than were loaded. */
  listsTruncated: boolean;
  /** What the SAVED campaign names — injected back so an archived list still renders by name. */
  stored: { include: AudienceListRef[]; exclude: AudienceListRef[] };
  include: number[];
  exclude: number[];
  onChange(next: { include: number[]; exclude: number[] }): void;
  disabled?: boolean;
  error?: string | null;
  /** Estimate state — the estimate runs against the SAVED audience. */
  estimate: ListPreview | null;
  estimating: boolean;
  estimateError: string | null;
  /** Why the Estimate button is disabled, or null when it is enabled. */
  estimateDisabledReason: string | null;
  onEstimate(): void;
}

function kindLabel(kind: 'segment' | 'static'): string {
  return kind === 'segment' ? 'Live segment' : 'Static list';
}

export function AudiencePicker({
  lists,
  listsLoading,
  listsError,
  listsTruncated,
  stored,
  include,
  exclude,
  onChange,
  disabled,
  error,
  estimate,
  estimating,
  estimateError,
  estimateDisabledReason,
  onEstimate,
}: AudiencePickerProps) {
  const allOptions = useMemo(() => {
    const byId = new Map<number, MultiComboboxOption>();
    for (const list of lists ?? []) {
      const count = list.memberCount === null ? '' : ` · ${list.memberCount.toLocaleString()}`;
      byId.set(list.id, {
        value: String(list.id),
        label: list.name,
        hint: `${kindLabel(list.kind)}${count}`,
      });
    }
    // A list the campaign already names may be archived or past the loaded page.
    for (const ref of [...stored.include, ...stored.exclude]) {
      if (!byId.has(ref.id)) {
        byId.set(ref.id, { value: String(ref.id), label: ref.name, hint: `${kindLabel(ref.kind)} · archived` });
      }
    }
    return byId;
  }, [lists, stored]);

  const includeSet = useMemo(() => new Set(include), [include]);
  const excludeSet = useMemo(() => new Set(exclude), [exclude]);

  const includeOptions = useMemo(
    () => [...allOptions.entries()].filter(([id]) => !excludeSet.has(id)).map(([, o]) => o),
    [allOptions, excludeSet],
  );
  const excludeOptions = useMemo(
    () => [...allOptions.entries()].filter(([id]) => !includeSet.has(id)).map(([, o]) => o),
    [allOptions, includeSet],
  );

  const footnote = listsError
    ? 'Lists could not be loaded.'
    : listsTruncated
      ? 'Showing the first 100 lists.'
      : undefined;

  const excluded = estimate?.excluded;
  const excludedTotal = excluded
    ? excluded.optedOut + excluded.suppressed + excluded.noEmail + excluded.invalidEmail
    : 0;

  return (
    <div className="grid min-w-0 gap-4">
      {error && (
        <p role="alert" className="m-0 text-xs text-danger">
          {error}
        </p>
      )}
      {listsError && (
        <Notice tone="danger" role="alert">
          Couldn&rsquo;t load lists. {listsError}
        </Notice>
      )}
      <div className="grid min-w-0 gap-4 md:grid-cols-2">
        <Field label="Send to">
          {({ id, 'aria-describedby': describedBy }) => (
            <MultiCombobox
              id={id}
              aria-describedby={describedBy}
              values={include.map(String)}
              onChange={(values) => onChange({ include: values.map(Number), exclude })}
              options={includeOptions}
              placeholder="Choose lists…"
              searchPlaceholder="Search lists…"
              loading={listsLoading}
              disabled={disabled}
              invalid={Boolean(error)}
              summaryNoun={{ singular: 'list', plural: 'lists' }}
              {...(footnote ? { footnote } : {})}
            />
          )}
        </Field>
        <Field label="Leave out">
          {({ id, 'aria-describedby': describedBy }) => (
            <MultiCombobox
              id={id}
              aria-describedby={describedBy}
              values={exclude.map(String)}
              onChange={(values) => onChange({ include, exclude: values.map(Number) })}
              options={excludeOptions}
              placeholder="No lists left out"
              searchPlaceholder="Search lists…"
              loading={listsLoading}
              disabled={disabled}
              summaryNoun={{ singular: 'list', plural: 'lists' }}
              {...(footnote ? { footnote } : {})}
            />
          )}
        </Field>
      </div>

      <div className="grid min-w-0 gap-3 rounded-md border border-border bg-surface-sunken px-3 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <span title={estimateDisabledReason ?? undefined} className="inline-flex">
            <Button
              variant="secondary"
              size="sm"
              onClick={onEstimate}
              loading={estimating}
              disabled={estimateDisabledReason !== null}
            >
              Estimate recipients
            </Button>
          </span>
          {estimateDisabledReason && !estimating && (
            <span className="text-xs text-fg-muted">{estimateDisabledReason}</span>
          )}
        </div>
        {estimateError && (
          <p role="alert" className="m-0 text-xs text-danger [overflow-wrap:anywhere]">
            Couldn&rsquo;t estimate. {estimateError}
          </p>
        )}
        {estimate && (
          <div className="grid min-w-0 gap-3">
            <p className="m-0 text-sm text-fg">
              <span className="text-lg font-semibold tabular">{estimate.count.toLocaleString()}</span>{' '}
              {estimate.count === 1 ? 'recipient' : 'recipients'}
              {excludedTotal > 0 && (
                <span className="text-fg-muted"> · {excludedTotal.toLocaleString()} left out</span>
              )}
            </p>
            {excluded && excludedTotal > 0 && (
              <dl className="m-0 grid grid-cols-2 gap-x-4 gap-y-1 text-xs sm:grid-cols-4">
                {(
                  [
                    ['Opted out', excluded.optedOut],
                    ['Suppressed', excluded.suppressed],
                    ['No email', excluded.noEmail],
                    ['Invalid email', excluded.invalidEmail],
                  ] as const
                ).map(([label, value]) => (
                  <div key={label} className="flex justify-between gap-2 sm:block">
                    <dt className="text-fg-muted">{label}</dt>
                    <dd className="m-0 tabular text-fg-secondary">{value.toLocaleString()}</dd>
                  </div>
                ))}
              </dl>
            )}
            {estimate.sample.length > 0 && (
              <div className="min-w-0">
                <p className="m-0 mb-1 text-xs font-medium text-fg-secondary">Sample</p>
                <ul className="m-0 grid list-none gap-1 p-0 text-xs">
                  {estimate.sample.slice(0, 10).map((member) => (
                    <li
                      key={`${member.type}-${member.id}`}
                      className="flex min-w-0 flex-wrap gap-x-2 text-fg-secondary"
                    >
                      <span className="font-medium text-fg [overflow-wrap:anywhere]">
                        {member.name.trim() || member.email}
                      </span>
                      <EmailLink address={member.email} tone="muted" />
                      {member.companyName && (
                        <span className="text-fg-subtle [overflow-wrap:anywhere]">{member.companyName}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
